import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const eze: ChauffeurPage = {
  slug: "eze",
  title: "VTC & chauffeur privé à Èze",
  subtitle:
    "Chauffeur privé à Èze : village médiéval, Jardin Exotique, La Chèvre d'Or et routes panoramiques des corniches.",
  keywords: ["vtc èze", "chauffeur privé èze", "chauffeur eze village"],
  hero: {
    title: "VTC & chauffeur privé à Èze",
    subtitle:
      "Le village perché de la Moyenne Corniche, entre Nice et Monaco : un chauffeur pour les routes panoramiques.",
    keywords: [
      "VTC Èze",
      "chauffeur Èze village",
      "chauffeur Moyenne Corniche",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & chauffeur privé à Èze",
    badges: [
      "Routes des corniches",
      "Èze Village & Èze-sur-Mer",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Èze",
    items: [
      {
        title: "Routes des corniches maîtrisées",
        description:
          "Village perché desservi par la Moyenne Corniche : virages, dénivelé et stationnement rare gérés par le chauffeur.",
      },
      {
        title: "Èze Village & Èze-sur-Mer",
        description:
          "Deux Èze à 400 m de dénivelé : le village médiéval en haut, la gare et les plages en bas, reliés sans effort.",
      },
      {
        title: "Restaurants & palaces d'altitude",
        description:
          "La Chèvre d'Or et son hôtel, le Château Eza : dépose au plus près des ruelles piétonnes, retour de dîner assuré.",
      },
      {
        title: "Entre Nice et Monaco",
        description:
          "Étape naturelle d'une journée Nice ↔ Monaco : visite du village puis poursuite vers la Principauté ou Cap-Ferrat.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur à Èze",
    services: [
      {
        title: "Visite du village perché",
        text: "Dépose au pied du village médiéval, attente pendant la visite du Jardin Exotique et reprise sans contrainte de parking.",
      },
      {
        title: "Transferts restaurant & hôtel",
        text: "La Chèvre d'Or (2 étoiles), Château Eza et tables d'altitude : aller et retour de dîner en toute sérénité.",
      },
      {
        title: "Routes panoramiques sur mesure",
        text: "Basse, Moyenne et Grande Corniche : itinéraire scénique entre Nice, Èze et Monaco selon l'heure et la lumière.",
      },
      {
        title: "Transferts aéroport",
        text: "Èze ↔ aéroport de Nice avec suivi de vol, par la corniche ou l'A8 selon le trafic.",
      },
      {
        title: "Èze-sur-Mer & gare",
        text: "Liaison entre le village et Èze-sur-Mer (gare, plages, départ du sentier de Nietzsche) en quelques minutes.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Èze Village",
        description:
          "Village médiéval perché, ruelles piétonnes et entrée du bourg.",
      },
      {
        name: "Jardin Exotique d'Èze",
        description:
          "Jardin de cactus au sommet du village, panorama sur la mer.",
      },
      {
        name: "La Chèvre d'Or",
        description: "Restaurant 2 étoiles et hôtel au cœur du village.",
      },
      {
        name: "Parfumerie Fragonard",
        description: "Usine et boutique de parfum en contrebas du village.",
      },
      {
        name: "Èze-sur-Mer",
        description:
          "Gare, plages et départ du sentier de Nietzsche vers le village.",
      },
      {
        name: "Moyenne Corniche",
        description: "Route panoramique reliant Nice, Èze et Monaco.",
      },
    ],
  },
  generic: {
    heading: "Èze, le nid d'aigle de la Moyenne Corniche",
    subheading:
      "Un village perché à 400 m au-dessus de la mer, où la route compte autant que la destination.",
    paragraphs: [
      "Èze se mérite : le village médiéval est accroché à un éperon rocheux de la Moyenne Corniche, au-dessus d'Èze-sur-Mer et de ses plages. Entre virages, dénivelé et stationnement rare aux abords du bourg, un chauffeur dépose au plus près de l'entrée piétonne et reprend à la demande, sans tourner pour se garer.",
      "La position d'Èze, à mi-chemin entre Nice et Monaco, en fait l'étape idéale d'une journée sur les corniches. Basse, Moyenne ou Grande Corniche : nous choisissons l'itinéraire le plus panoramique selon l'heure et la lumière, avec arrêt au Jardin Exotique ou à la parfumerie Fragonard.",
      "Pour un déjeuner ou un dîner à La Chèvre d'Or ou au Château Eza, le chauffeur gère l'aller comme le retour de nuit par la corniche, sans contrainte de conduite après le repas.",
    ],
    bullets: [
      "Itinéraire corniches",
      "Dépose pied du village",
      "Retour de dîner",
    ],
  },
  process: {
    heading: "Réserver un chauffeur à Èze",
    steps: [
      "Indiquez votre programme (village, restaurant, Èze-sur-Mer, aéroport).",
      "Nous proposons l'itinéraire corniche le plus adapté à l'heure et au trafic.",
      "Le chauffeur dépose au pied du village ou à l'adresse du restaurant.",
      "Visite ou dîner à votre rythme, reprise à la demande pour la suite du programme.",
    ],
  },
  faq: [
    {
      q: "Peut-on accéder au village d'Èze en voiture ?",
      a: "Le cœur du village médiéval est entièrement piéton. Le chauffeur vous dépose au plus près de l'entrée du bourg, sur la Moyenne Corniche, puis vous reprend au même endroit après la visite.",
    },
    {
      q: "Quelle est la différence entre Èze Village et Èze-sur-Mer ?",
      a: "Èze Village est le village perché sur la Moyenne Corniche ; Èze-sur-Mer est au niveau de la mer, avec la gare et les plages. Près de 400 m de dénivelé les séparent — le chauffeur relie les deux en quelques minutes.",
    },
    {
      q: "Proposez-vous un itinéraire panoramique par les corniches ?",
      a: "Oui. Selon l'heure et la lumière, nous empruntons la Basse, la Moyenne ou la Grande Corniche entre Nice, Èze et Monaco, avec arrêts photo possibles aux points de vue.",
    },
    {
      q: "Assurez-vous le retour après un dîner à La Chèvre d'Or ?",
      a: "Oui, l'aller comme le retour de nuit sont assurés par la corniche. Vous profitez du dîner sans contrainte de conduite, le chauffeur vous reprend à l'heure convenue.",
    },
  ],
  relatedLinks: [
    {
      label: "Transfert Monaco ⇄ Aéroport de Nice",
      href: "/chauffeur/transferts/monaco-aeroport-nice",
    },
    {
      label: "Transfert Aéroport de Nice ⇄ Saint-Jean-Cap-Ferrat",
      href: "/chauffeur/transferts/aeroport-nice-saint-jean-cap-ferrat",
    },
    {
      label: "Tous les transferts de la Côte d’Azur",
      href: "/chauffeur/transferts",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Èze",
    text: "Village perché, dîner étoilé ou route des corniches : réservez votre chauffeur pour Èze.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title:
      "VTC Èze — chauffeur privé village perché & corniches | KS Rent Services",
    description:
      "VTC et chauffeur privé à Èze : village médiéval, Jardin Exotique, La Chèvre d'Or et routes des corniches entre Nice et Monaco. Suivi de vol, 24/7. Réservez.",
    canonical: "/chauffeur/eze",
  },
};

export default eze;
